import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { authApi } from '@/services/api';
import { OtpInput } from '@/components/OtpInput';
import { useAuthStore } from '@/stores/authStore';
import { saveTrustedDeviceToken } from '@/services/trustedDevice';
import {
  TruckIcon,
  ShieldCheckIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

interface Channel {
  channel: 'sms' | 'email';
  available: boolean;
  destination: string;
}

interface LocationState {
  loginSessionToken: string;
  channels: Channel[];
  expiresInSec: number;
  email: string;
}

/**
 * Step 2 of login: the user enters the OTP code the server sent.
 * On success the response carries { user, tokens } same shape as a
 * password-only login would have produced, so we hand off to setAuth.
 */
export default function OtpVerify() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setAuth } = useAuthStore();
  const state = location.state as LocationState | null;

  const [code, setCode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [trustDevice, setTrustDevice] = useState(false);
  const [activeChannel, setActiveChannel] = useState<'sms' | 'email'>('sms');
  const [remaining, setRemaining] = useState(state?.expiresInSec || 300);
  const [resendCooldown, setResendCooldown] = useState(0);
  const sentInitial = useRef(false);

  useEffect(() => {
    if (!state?.loginSessionToken) {
      navigate('/login', { replace: true });
    }
  }, [state, navigate]);

  useEffect(() => {
    if (sentInitial.current || !state?.loginSessionToken) return;
    const firstAvailable = state.channels.find((c) => c.available);
    if (!firstAvailable) return;
    sentInitial.current = true;
    setActiveChannel(firstAvailable.channel);
    sendCode(firstAvailable.channel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  useEffect(() => {
    if (remaining <= 0) return;
    const id = setInterval(() => setRemaining((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [remaining]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  const sendCode = async (channel: 'sms' | 'email') => {
    if (!state?.loginSessionToken) return;
    setIsSending(true);
    try {
      await authApi.sendOtp(state.loginSessionToken, channel);
      toast.success(channel === 'sms' ? 'تم إرسال الرمز عبر الرسالة' : 'تم إرسال الرمز عبر البريد');
      setResendCooldown(30);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'فشل إرسال الرمز';
      toast.error(msg);
    } finally {
      setIsSending(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!state?.loginSessionToken) return;
    if (code.length < 6) {
      toast.error('أدخل الرمز كاملاً');
      return;
    }
    setIsSubmitting(true);
    try {
      const resp = await authApi.verifyOtp(state.loginSessionToken, code, trustDevice);
      const result = resp.data.data;
      if (result?.user && result?.tokens) {
        // The server mints this only when trustDevice was set. Persisting it
        // is what actually makes the checkbox work — it used to be returned
        // and discarded, so the box created a trusted-device record that was
        // never presented again and every login still demanded a code.
        if (result.deviceToken?.token) {
          saveTrustedDeviceToken(
            state.email || result.user.email,
            result.deviceToken.token,
            result.deviceToken.expiresAt,
          );
        }
        setAuth(result.user, result.tokens.accessToken, result.tokens.refreshToken);
        toast.success('تم تسجيل الدخول بنجاح');
        navigate('/');
      } else {
        toast.error('استجابة الخادم غير متوقعة');
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || 'الرمز غير صحيح';
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!state?.loginSessionToken) return null;

  const mins = Math.floor(remaining / 60);
  const secs = remaining % 60;
  const activeDest = state.channels.find((c) => c.channel === activeChannel)?.destination;

  return (
    <div className="min-h-screen flex" dir="rtl">
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl gradient-primary mb-4">
              <ShieldCheckIcon className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">التحقق من الهوية</h1>
            <p className="text-gray-500 mt-2 text-sm">
              أدخل رمز التحقق المرسل إلى{' '}
              <span className="font-medium text-gray-700" dir="ltr">{activeDest}</span>
            </p>
          </div>

          {state.channels.filter((c) => c.available).length > 1 && (
            <div className="mb-4 flex gap-2 justify-center">
              {state.channels.filter((c) => c.available).map((c) => (
                <button
                  key={c.channel}
                  type="button"
                  onClick={() => { setActiveChannel(c.channel); sendCode(c.channel); }}
                  className={`px-3 py-1.5 rounded-lg text-sm border ${
                    activeChannel === c.channel
                      ? 'bg-primary-50 border-primary-300 text-primary-700'
                      : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {c.channel === 'sms' ? 'رسالة نصية' : 'بريد إلكتروني'}
                </button>
              ))}
            </div>
          )}

          <form onSubmit={handleVerify} className="space-y-5">
            <div>
              <label className="label">رمز التحقق</label>
              {/* Six separate boxes with paste and arrow-key handling, rather
                  than one free-text field. The component existed but nothing
                  ever imported it. */}
              <OtpInput
                length={6}
                value={code}
                onChange={setCode}
                disabled={isSubmitting || remaining === 0}
              />
              <p className="text-xs text-gray-500 mt-2 text-center">
                ينتهي الرمز بعد:{' '}
                <span className={remaining < 30 ? 'text-red-600 font-semibold' : 'text-gray-700'}>
                  {mins}:{secs.toString().padStart(2, '0')}
                </span>
              </p>
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={trustDevice}
                onChange={(e) => setTrustDevice(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span>اعتبار هذا الجهاز موثوقاً</span>
            </label>

            <button
              type="submit"
              disabled={isSubmitting || code.length < 6 || remaining === 0}
              className="btn-primary w-full disabled:opacity-50"
            >
              {isSubmitting ? <div className="spinner w-5 h-5" /> : 'تأكيد الدخول'}
            </button>

            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={() => sendCode(activeChannel)}
                disabled={isSending || resendCooldown > 0}
                className="text-primary-600 hover:text-primary-700 disabled:text-gray-400 flex items-center gap-1"
              >
                <ArrowPathIcon className="w-4 h-4" />
                {resendCooldown > 0 ? `إعادة الإرسال خلال ${resendCooldown} ث` : 'إعادة إرسال الرمز'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="text-gray-500 hover:text-gray-700"
              >
                العودة لتسجيل الدخول
              </button>
            </div>
          </form>

          <p className="text-center text-sm text-gray-500 mt-8">
            © {new Date().getFullYear()} وزارة الصحة - جميع الحقوق محفوظة
          </p>
        </div>
      </div>

      <div className="hidden lg:flex lg:flex-1 gradient-primary items-center justify-center p-12">
        <div className="max-w-lg text-center text-white">
          <div className="w-32 h-32 mx-auto mb-8 rounded-full bg-white/10 flex items-center justify-center">
            <TruckIcon className="w-16 h-16" />
          </div>
          <h2 className="text-3xl font-bold mb-4">نظام تتبع وإدارة سيارات الإسعاف</h2>
          <p className="text-lg text-white/80 leading-relaxed">
            خطوة تحقق إضافية لحماية حسابك. الرمز صالح لـ 5 دقائق فقط.
          </p>
        </div>
      </div>
    </div>
  );
}
